import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getCurrentUser } from "../utils/auth";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

export default function WalmartDcAddressBook() {
  const [status, setStatus] = useState({ record_count: 0, last_imported_at: null });
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  const user = getCurrentUser();
  const headers = useMemo(() => {
    const next = {};
    if (user?.email) next["X-User-Email"] = user.email;
    if (user?.role) next["X-User-Role"] = user.role || "";
    return next;
  }, [user?.email, user?.role]);

  const fetchStatus = useCallback(async () => {
    setError("");
    try {
      const resp = await fetch(`${API_BASE}/api/admin/walmart-dc-address-book/status`, { headers });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload?.error || "Unable to load status.");
      }
      setStatus({
        record_count: payload?.record_count || 0,
        last_imported_at: payload?.last_imported_at || null,
      });
    } catch (err) {
      setError(err?.message || "Unable to load status.");
    }
  }, [headers]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleImport = async () => {
    if (!selectedFile || importing) return;
    setImporting(true);
    setError("");
    setSummary(null);
    try {
      const form = new FormData();
      form.append("file", selectedFile);
      const resp = await fetch(`${API_BASE}/api/admin/walmart-dc-address-book/import`, {
        method: "POST",
        headers,
        body: form,
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload?.error || "Import failed.");
      }
      setSummary(payload);
      await fetchStatus();
    } catch (err) {
      setError(err?.message || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-800">Walmart DC Address Book</h2>
        <p className="mt-1 text-sm text-slate-500">
          Upload the Walmart DC Address Book XLSX to keep Ship To addresses up to date.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700">Current Status</h3>
        <div className="mt-2 text-sm text-slate-600">
          <div>Records: {status.record_count}</div>
          <div>Last Imported: {status.last_imported_at || "Not available"}</div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700">Import Address Book</h3>
        <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center">
          <input
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
            className="block w-full rounded-md border border-slate-300 text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-200"
          />
          <button
            type="button"
            onClick={handleImport}
            disabled={!selectedFile || importing}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {importing ? "Importing..." : "Import"}
          </button>
        </div>
        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
        {summary && (
          <div className="mt-4 rounded-md border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-800">
            <div>Total Rows: {summary.total_rows ?? 0}</div>
            <div>Inserted: {summary.inserted ?? 0}</div>
            <div>Updated: {summary.updated ?? 0}</div>
            <div>Skipped: {summary.skipped ?? 0}</div>
            {summary.errors?.length ? (
              <div className="mt-2 text-emerald-900">
                <div className="font-semibold">Errors:</div>
                <ul className="list-disc pl-5">
                  {summary.errors.map((item, idx) => (
                    <li key={`err-${idx}`}>
                      {item?.row ? `Row ${item.row}: ` : ""}{item?.error || item?.message || "Unknown error"}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
